import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";
import { useEffect, useState } from "react";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import {
    add_inspection_settings,
    backend_url,
    get_all_inspection_settings,
    GET_CURRENCIES_URL,
    get_inspection_categories // ADD THIS LINE
    ,


    update_inspection_settings
} from "../../../utils/urls";

const HandoverInspectionTab = ({ facilityId, unitId, showListView = true, autoOpenModal = false, context = "default", onItemAdded }) => {
    const [inspectionItems, setInspectionItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [categories, setCategories] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editItemId, setEditItemId] = useState(null);
    const [showRemoved, setShowRemoved] = useState(false);
    const [currencies, setCurrencies] = useState([]);
    const [defaultCurrency, setDefaultCurrency] = useState(null);

    // Default conditions list
    const defaultConditions = ["Excellent", "Good", "Fair", "Poor", "Damaged", "Non-functional"];

    const [newItem, setNewItem] = useState({
        name: "",
        category: "",
        description: "",
        possibleConditions: ["Excellent", "Good", "Fair", "Poor", "Damaged", "Non-functional"],
        defaultCondition: "Good",
        isRequired: true,
        defaultQuantity: 1,
        serialNumber: "",
        cost: "",
        currencyId: "",
        images: [],
        active: true,
        ...(context === "moveOut" && {
            condition: "Good",
            repairCost: 0,
            repairDescription: "",
            requiresRepair: false
        })
    });

    // Fetch currencies for this facility
    const fetchCurrencies = async () => {
        if (!facilityId) return;

        try {
            const response = await makeRequest2(
                `${GET_CURRENCIES_URL}/${facilityId}`,
                "GET"
            );

            console.log("Currency API Response:", response); // Debug log

            if (response.success && response.data) {
                const currencyData = response.data.data || response.data;
                const currenciesArray = Array.isArray(currencyData)
                    ? currencyData
                    : [currencyData];

                console.log("Processed currencies array:", currenciesArray); // Debug log
                setCurrencies(currenciesArray);

                // Find default currency
                const defaultCurr = currenciesArray.find(curr => curr.isDefaultCurrency);
                if (defaultCurr) {
                    setDefaultCurrency(defaultCurr);
                } else if (currenciesArray.length > 0) {
                    // Use first currency as fallback
                    setDefaultCurrency(currenciesArray[0]);
                }
            } else {
                console.error("Currency fetch failed:", response);
                toastify("Failed to fetch currencies", "error");
            }
        } catch (error) {
            console.error("Error fetching currencies:", error);
            toastify("Error loading currencies", "error");
        }
    };

    // Fetch inspection categories for this facility
    const fetchInspectionCategories = async () => {
        if (!facilityId) {
            console.log("fetchInspectionCategories: No facilityId provided");
            return;
        }

        try {
            console.log(`Fetching inspection categories for facility: ${facilityId}`);

            const response = await makeRequest2(
                `${get_inspection_categories}/${facilityId}`,
                "GET"
            );

            console.log("Inspection categories API response:", response);

            if (response.success && response.data) {
                // Backend returns: { success: true, data: { success: true, data: [...categories], count: X, message: "..." } }
                let categoriesData = [];

                // Handle nested data structure
                if (response.data.data && Array.isArray(response.data.data)) {
                    // Data is nested in response.data.data
                    categoriesData = response.data.data;
                } else if (Array.isArray(response.data)) {
                    // If data is directly an array
                    categoriesData = response.data;
                } else if (typeof response.data === 'object') {
                    // If it's an object, try to extract array
                    categoriesData = Object.values(response.data).filter(item =>
                        item && typeof item === 'object' && item.name
                    );
                }

                console.log("Extracted categories data:", categoriesData);

                // Extract category names from active categories only
                const categoryNames = categoriesData
                    .filter(cat => cat && cat.name && cat.active !== false) // Only active categories
                    .map(cat => cat.name);

                console.log(`Found ${categoryNames.length} active inspection categories:`, categoryNames);
                setCategories(categoryNames);

                if (categoryNames.length === 0) {
                    console.warn("No active categories found for this facility");
                    toastify("No active categories found. Please add categories first.", "warning");
                }
            } else {
                console.error("Failed to fetch categories:", response.error || "Unknown error");
                toastify("Failed to fetch categories. Categories will be extracted from inspection items.", "warning");
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            toastify("Error loading categories. Categories will be extracted from inspection items.", "warning");
        }
    };
    // Fetch inspection items for this unit
    const fetchInspectionItems = async () => {
        if (!facilityId || !unitId) {
            console.log("Missing facilityId or unitId:", { facilityId, unitId });
            return;
        }

        try {
            setLoading(true);
            console.log("Fetching inspection items for facility:", facilityId);

            const response = await makeRequest2(
                `${get_all_inspection_settings}/${facilityId}`,
                "GET"
            );
            setLoading(false);

            console.log("Inspection items API response:", response);

            if (response.success) {
                // Ensure data is an array
                const allItems = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.data || []);

                console.log("All inspection items:", allItems);

                // Filter for this unit only
                const unitItems = allItems.filter(item => {
                    // Handle cases where unitId might be null, undefined, or an object
                    if (!item.unitId) return false;

                    const itemUnitId = typeof item.unitId === 'object' && item.unitId !== null
                        ? item.unitId._id
                        : item.unitId;

                    return itemUnitId === unitId;
                });

                console.log("Unit-specific items:", unitItems);
                setInspectionItems(unitItems);

                // Extract categories from unit-specific items
                const allCategories = [...new Set(unitItems.map(item => item.category).filter(Boolean))];
                console.log("Categories extracted from items:", allCategories);

                // Merge with existing categories instead of replacing them
                if (allCategories.length > 0) {
                    setCategories(prevCategories => {
                        // Only merge if we have previously fetched categories
                        if (prevCategories.length > 0) {
                            const merged = [...new Set([...prevCategories, ...allCategories])];
                            console.log("Merged categories (API + Items):", merged);
                            return merged;
                        }
                        // If no previous categories, use the ones from items
                        return allCategories;
                    });
                }
            } else {
                console.error("Failed to fetch inspection items:", response);
                toastify("Failed to fetch inspection items", "error");
            }
        } catch (error) {
            setLoading(false);
            console.error("Error fetching inspection items:", error);
            toastify("Failed to fetch inspection items", "error");
        }
    };

    // Set default condition - we don't need to select/deselect conditions anymore
    const setAsDefault = (condition) => {
        setNewItem({
            ...newItem,
            defaultCondition: condition
        });
    };

    // Handle editing an item
    const handleEdit = (rowData) => {
        console.log("Editing item:", rowData); // Debug log
        setIsEditing(true);
        setEditItemId(rowData._id);
        setNewItem({
            name: rowData.name,
            category: rowData.category,
            description: rowData.description || "",
            possibleConditions: defaultConditions,
            defaultCondition: rowData.defaultCondition || "Good",
            isRequired: rowData.isRequired !== undefined ? rowData.isRequired : true,
            defaultQuantity: rowData.defaultQuantity || 1,
            serialNumber: rowData.serialNumber || "",
            cost: rowData.cost || "",
            currencyId: (typeof rowData.currencyId === 'object' && rowData.currencyId ? rowData.currencyId._id : rowData.currencyId) || (defaultCurrency ? defaultCurrency._id : "") || "",
            images: rowData.images || [],
            active: rowData.active !== undefined ? rowData.active : true
        });
        console.log("Set currencyId to:", (typeof rowData.currencyId === 'object' && rowData.currencyId ? rowData.currencyId._id : rowData.currencyId) || (defaultCurrency ? defaultCurrency._id : "") || ""); // Debug log
        setModalOpen(true);
    };

    // Soft delete/remove item (mark as inactive)
    const handleRemoveItem = async (itemId) => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `${update_inspection_settings}/${facilityId}/${itemId}`,
                "PUT",
                { active: false }
            );
            setLoading(false);

            if (response.success) {
                toastify("Item removed from list successfully", "success");
                fetchInspectionItems();
            } else {
                toastify(response.error || "Failed to remove item", "error");
            }
        } catch (error) {
            setLoading(false);
            console.error("Error removing item:", error);
            toastify("Failed to remove item", "error");
        }
    };

    // Restore item (mark as active)
    const handleRestoreItem = async (itemId) => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `${update_inspection_settings}/${facilityId}/${itemId}`,
                "PUT",
                { active: true }
            );
            setLoading(false);

            if (response.success) {
                toastify("Item restored to list successfully", "success");
                fetchInspectionItems();
            } else {
                toastify(response.error || "Failed to restore item", "error");
            }
        } catch (error) {
            setLoading(false);
            console.error("Error restoring item:", error);
            toastify("Failed to restore item", "error");
        }
    };

    // Add or update inspection item
    const saveInspectionItem = async () => {
        console.log("SaveInspectionItem called with:", newItem);

        if (!newItem.name || !newItem.category) {
            toastify("Item name and category are required", "error");
            return;
        }

        console.log("Validation passed, proceeding to save");

        try {
            setLoading(true);

            // Prepare FormData for file uploads
            const formData = new FormData();

            // Add regular fields
            formData.append('name', newItem.name);
            formData.append('category', newItem.category);
            formData.append('description', newItem.description || '');
            formData.append('possibleConditions', JSON.stringify(newItem.possibleConditions));
            formData.append('defaultCondition', newItem.defaultCondition);
            formData.append('isRequired', newItem.isRequired);
            formData.append('defaultQuantity', newItem.defaultQuantity);
            formData.append('serialNumber', newItem.serialNumber || '');
            formData.append('cost', parseFloat(newItem.cost) || 0);
            formData.append('active', newItem.active !== undefined ? newItem.active : true);

            // Add unitId if available
            if (unitId) {
                formData.append('unitId', unitId);
            }

            // Add currencyId if selected or use default
            const selectedCurrencyId = newItem.currencyId || (defaultCurrency ? defaultCurrency._id : null);
            console.log("Sending currencyId:", selectedCurrencyId); // Debug log
            if (selectedCurrencyId) {
                formData.append('currencyId', selectedCurrencyId);
            } else {
                console.log("No currencyId to send - newItem.currencyId:", newItem.currencyId, "defaultCurrency:", defaultCurrency);
            }

            // Handle images: send existing image URLs to keep + new files to upload
            if (isEditing) {
                // For edit mode: send list of existing images to keep
                const existingImagesToKeep = newItem.images
                    .filter(img => typeof img === 'string') // Existing images are URL strings
                    .map(url => url);

                if (existingImagesToKeep.length > 0) {
                    formData.append('existingImages', JSON.stringify(existingImagesToKeep));
                }
            }

            // Add NEW image files
            if (newItem.images && newItem.images.length > 0) {
                newItem.images.forEach((imageData) => {
                    // Only send new files (those with .file property)
                    if (imageData.file) {
                        formData.append('images', imageData.file);
                    }
                });
            }

            console.log("Sending FormData to API with files");

            let response;
            if (isEditing) {
                response = await makeRequest2(
                    `${update_inspection_settings}/${facilityId}/${editItemId}`,
                    "PUT",
                    formData,
                    {
                        headers: {
                            // Don't set Content-Type, let browser set it with boundary for FormData
                        }
                    }
                );
            } else {
                response = await makeRequest2(
                    `${add_inspection_settings}/${facilityId}`,
                    "POST",
                    formData,
                    {
                        headers: {
                            // Don't set Content-Type, let browser set it with boundary for FormData
                        }
                    }
                );
            }

            setLoading(false);

            // Check for various success conditions including 201 status code
            if (response.success || (response.error && response.error.includes('201'))) {
                toastify(`Item ${isEditing ? 'updated' : 'added'} successfully`, "success");
                setModalOpen(false);
                // Reset form
                setNewItem({
                    name: "",
                    category: "",
                    description: "",
                    possibleConditions: ["Excellent", "Good", "Fair", "Poor", "Damaged", "Non-functional"],
                    defaultCondition: "Good",
                    isRequired: true,
                    defaultQuantity: 1,
                    serialNumber: "",
                    cost: "",
                    currencyId: (defaultCurrency && defaultCurrency._id) ? defaultCurrency._id : "",
                    images: [],
                    active: true,
                    ...(context === "moveOut" && {
                        condition: "Good",
                        repairCost: 0,
                        repairDescription: "",
                        requiresRepair: false
                    })
                });
                setIsEditing(false);
                setEditItemId(null);
                fetchInspectionItems();

                // Call callback if provided (for handover integration)
                console.log('HandoverInspectionTab: Checking onItemAdded callback', {
                    hasCallback: !!onItemAdded,
                    isEditing,
                    context
                });
                if (onItemAdded && !isEditing && (context === "moveOut" || context === "moveIn")) {
                    // Create base item structure using response data (which has saved image URLs)
                    const addedItem = {
                        _id: response.data?._id || Date.now(),
                        name: newItem.name,
                        category: newItem.category,
                        description: newItem.description || '',
                        condition: newItem.condition || newItem.defaultCondition,
                        quantity: newItem.defaultQuantity || 1,
                        serialNumber: newItem.serialNumber || '',
                        cost: parseFloat(newItem.cost) || 0,
                        currencyId: newItem.currencyId,
                        images: response.data?.images || []  // Use images from backend response
                    };

                    // Add context-specific fields
                    if (context === "moveOut") {
                        addedItem.repairCost = newItem.repairCost || 0;
                        addedItem.repairDescription = newItem.repairDescription || '';
                        addedItem.requiresRepair = newItem.requiresRepair || false;
                        addedItem.originalCondition = newItem.defaultCondition;
                        addedItem.currentCondition = newItem.condition;
                    }

                    console.log('HandoverInspectionTab: Calling onItemAdded with:', addedItem);
                    onItemAdded(addedItem);
                }
            } else {
                console.error("API Error:", response);
                toastify(response.error || `Failed to ${isEditing ? 'update' : 'add'} item`, "error");
            }
        } catch (error) {
            setLoading(false);
            console.error(`Error ${isEditing ? 'updating' : 'adding'} item:`, error);
            toastify(error.message || `Failed to ${isEditing ? 'update' : 'add'} item`, "error");
        }
    };

    // Filter items by category and active status
    const filteredItems = inspectionItems.filter(item => {
        const categoryMatch = selectedCategory === "All" || item.category === selectedCategory;
        const statusMatch = showRemoved ? item.active === false : item.active !== false;
        return categoryMatch && statusMatch;
    });

    useEffect(() => {
        if (facilityId && unitId) {
            fetchInspectionItems();
            fetchCurrencies();
            fetchInspectionCategories();
        }
    }, [facilityId, unitId]);

    // Auto-open modal when autoOpenModal is true (for move-in handover use case)
    useEffect(() => {
        if (autoOpenModal && facilityId && unitId && !showListView) {
            // Reset the form and open modal
            setIsEditing(false);
            setEditItemId(null);
            setNewItem({
                name: "",
                category: "",
                description: "",
                possibleConditions: ["Excellent", "Good", "Fair", "Poor", "Damaged", "Non-functional"],
                defaultCondition: "Good",
                isRequired: true,
                defaultQuantity: 1,
                serialNumber: "",
                cost: "",
                currencyId: (defaultCurrency && defaultCurrency._id) ? defaultCurrency._id : "",
                images: [],
                active: true,
                ...(context === "moveOut" && {
                    condition: "Good",
                    repairCost: 0,
                    repairDescription: "",
                    requiresRepair: false
                })
            });
            setModalOpen(true);
        }
    }, [autoOpenModal, facilityId, unitId, showListView, defaultCurrency, context]);

    if (!facilityId || !unitId) {
        return <div className="card">Please select a facility and unit</div>;
    }

    // Render selected condition only
    const conditionsTemplate = (rowData) => {
        const defaultCondition = rowData.defaultCondition || 'Good';
        return (
            <Tag
                value={defaultCondition}
                severity={
                    ['Excellent', 'Good'].includes(defaultCondition) ? 'success' :
                        defaultCondition === 'Fair' ? 'warning' : 'danger'
                }
            />
        );
    };

    // Render actions column
    const actionsTemplate = (rowData) => {
        return (
            <div className="d-flex gap-2 justify-content-end">
                <Button
                    icon="ti ti-edit"
                    className="p-button-rounded p-button-info p-button-sm"
                    onClick={() => handleEdit(rowData)}
                    tooltip="Edit"
                    style={{ width: '2.5rem', height: '2.5rem' }}
                />
                {rowData.active !== false ? (
                    <Button
                        icon="ti ti-archive"
                        className="p-button-rounded p-button-warning p-button-sm"
                        onClick={() => handleRemoveItem(rowData._id)}
                        tooltip="Remove from list"
                        style={{ width: '2.5rem', height: '2.5rem' }}
                    />
                ) : (
                    <Button
                        icon="ti ti-refresh"
                        className="p-button-rounded p-button-success p-button-sm"
                        onClick={() => handleRestoreItem(rowData._id)}
                        tooltip="Restore to list"
                        style={{ width: '2.5rem', height: '2.5rem' }}
                    />
                )}
            </div>
        );
    };

    // Empty template
    const emptyTemplate = () => {
        return (
            <div className="text-center py-4">
                <div className="text-muted">
                    <i className="ti ti-inbox mb-2" style={{ fontSize: "24px" }}></i>
                    <p className="mb-0">No inspection items found</p>
                    <p>Add your first item to get started</p>
                </div>
            </div>
        );
    };

    return (
        <div>
            {/* Inspection Items List - only show when showListView is true */}
            {showListView && (
                <div>
                    {/* Warning about adding new items after handovers exist */}
                    <div className="alert alert-warning mb-3" role="alert">
                        <i className="ti ti-alert-triangle me-2"></i>
                        <strong>Important:</strong> Items added here will be available for future handovers.
                        If you add new items after creating move-in handovers, they will appear as "Additional" items in move-out handovers
                        and cannot have damage charges applied (since they weren't in the original move-in inspection).
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h5>Handover Inspection Items</h5>
                            <div className="d-flex gap-3 mt-2">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="showRemoved"
                                        checked={showRemoved}
                                        onChange={(e) => setShowRemoved(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="showRemoved">
                                        Show removed items
                                    </label>
                                </div>
                            </div>
                        </div>
                        <button className="btn btn-primary" onClick={() => {
                            console.log("Add New Item button clicked");
                            setIsEditing(false);
                            setEditItemId(null);
                            setNewItem({
                                name: "",
                                category: "",
                                description: "",
                                possibleConditions: ["Excellent", "Good", "Fair", "Poor", "Damaged", "Non-functional"],
                                defaultCondition: "Good",
                                isRequired: true,
                                defaultQuantity: 1,
                                serialNumber: "",
                                cost: "",
                                currencyId: (defaultCurrency && defaultCurrency._id) ? defaultCurrency._id : "",
                                images: [],
                                active: true,
                                ...(context === "moveOut" && {
                                    condition: "Good",
                                    repairCost: 0,
                                    repairDescription: "",
                                    requiresRepair: false
                                })
                            });
                            setModalOpen(true);
                            console.log("Modal should be open now, modalOpen set to true");
                        }}>
                            <i className="ti ti-plus me-1"></i> Add New Item
                        </button>
                    </div>

                    <DataTable
                        value={Array.isArray(filteredItems) ? filteredItems : []}
                        loading={loading && inspectionItems.length === 0}
                        emptyMessage={emptyTemplate}
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25]}
                        className="p-datatable-sm"
                    >
                        <Column field="name" header="NAME" sortable />
                        <Column field="category" header="CATEGORY" sortable />
                        <Column
                            field="cost"
                            header="INSPECTION COST"
                            sortable
                            body={(rowData) => {
                                if (!rowData.cost || parseFloat(rowData.cost) === 0) {
                                    return <span>N/A</span>;
                                }

                                // Find the currency for this item
                                let itemCurrency = defaultCurrency; // Default fallback

                                if (rowData.currencyId) {
                                    // If currencyId is populated (object), use it directly
                                    if (typeof rowData.currencyId === 'object' && rowData.currencyId !== null && rowData.currencyId.currencyShortCode) {
                                        itemCurrency = rowData.currencyId;
                                    } else if (typeof rowData.currencyId === 'string') {
                                        // If currencyId is just an ID, find it in currencies array
                                        const foundCurrency = currencies.find(curr => curr._id === rowData.currencyId);
                                        if (foundCurrency) {
                                            itemCurrency = foundCurrency;
                                        }
                                    }
                                }

                                const currencyCode = itemCurrency?.currencyShortCode || '$';
                                return <span>{currencyCode}{parseFloat(rowData.cost).toFixed(2)}</span>;
                            }}
                        />
                        <Column
                            header="IMAGES"
                            body={(rowData) => (
                                <div>
                                    {rowData.images && rowData.images.length > 0 ? (
                                        <Tag value={`${rowData.images.length} image(s)`} severity="info" />
                                    ) : (
                                        <Tag value="No images" severity="warning" />
                                    )}
                                </div>
                            )}
                        />
                        <Column header="CONDITIONS" body={conditionsTemplate} />
                        <Column
                            header="STATUS"
                            body={(rowData) => (
                                <Tag
                                    value={rowData.active !== false ? 'Active' : 'Removed'}
                                    severity={rowData.active !== false ? 'success' : 'danger'}
                                />
                            )}
                            sortable
                        />
                        <Column body={actionsTemplate} header="ACTIONS" style={{ width: '120px' }} />
                    </DataTable>
                </div>
            )}

            {/* Add/Edit Item Modal */}
            {modalOpen && (
                <>
                    <div className="modal show fade" style={{ display: "block" }} tabIndex="-1">
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">{isEditing ? 'Edit' : 'Add'} Inspection Item</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setModalOpen(false)}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <form>
                                        <div className="mb-3">
                                            <label className="form-label">Item Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={newItem.name}
                                                onChange={(e) =>
                                                    setNewItem({ ...newItem, name: e.target.value })
                                                }
                                                required
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label">Category <span className="text-danger">*</span></label>
                                            <select
                                                className="form-select"
                                                value={newItem.category}
                                                onChange={(e) =>
                                                    setNewItem({ ...newItem, category: e.target.value })
                                                }
                                                required
                                            >
                                                <option value="">Select Category</option>
                                                {categories.map((cat, index) => (
                                                    <option key={index} value={cat}>{cat}</option>
                                                ))}
                                            </select>
                                        </div>


                                        <div className="mb-3">
                                            <label className="form-label">Description</label>
                                            <textarea
                                                className="form-control"
                                                value={newItem.description || ""}
                                                onChange={(e) =>
                                                    setNewItem({ ...newItem, description: e.target.value })
                                                }
                                                rows="2"
                                            ></textarea>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Default Quantity</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={newItem.defaultQuantity || 1}
                                                        onChange={(e) =>
                                                            setNewItem({ ...newItem, defaultQuantity: parseInt(e.target.value) || 1 })
                                                        }
                                                        min="1"
                                                        placeholder="1"
                                                    />
                                                    <small className="text-muted">Default quantity for this item in handovers</small>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Serial Number</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={newItem.serialNumber || ""}
                                                        onChange={(e) =>
                                                            setNewItem({ ...newItem, serialNumber: e.target.value })
                                                        }
                                                        placeholder="Enter serial number (if applicable)"
                                                    />
                                                    <small className="text-muted">Serial number or reference code</small>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label">
                                                Item Cost
                                            </label>
                                            <div className="row">
                                                <div className="col-4">
                                                    <select
                                                        className="form-select"
                                                        value={newItem.currencyId || ""}
                                                        onChange={(e) =>
                                                            setNewItem({ ...newItem, currencyId: e.target.value })
                                                        }
                                                    >
                                                        <option value="">Select Currency</option>
                                                        {currencies.map((currency) => (
                                                            <option key={currency._id} value={currency._id}>
                                                                {currency.currencyShortCode} - {currency.currencyName}
                                                                {currency.isDefaultCurrency ? ' (Default)' : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-8">
                                                    <div className="input-group">
                                                        {newItem.currencyId && (
                                                            <span className="input-group-text">
                                                                {currencies.find(curr => curr._id === newItem.currencyId)?.currencyShortCode || '$'}
                                                            </span>
                                                        )}
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            className="form-control"
                                                            value={newItem.cost || ""}
                                                            onChange={(e) =>
                                                                setNewItem({ ...newItem, cost: e.target.value })
                                                            }
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <small className="text-muted">Cost for this item (different from repair costs during move-out)</small>
                                        </div>

                                        {/* Move-out specific fields */}
                                        {context === "moveOut" && (
                                            <>
                                                <div className="mb-3">
                                                    <label className="form-label">Current Condition <span className="text-danger">*</span></label>
                                                    <select
                                                        className="form-select"
                                                        value={newItem.condition || "Good"}
                                                        onChange={(e) => {
                                                            const newCondition = e.target.value;
                                                            const resetRepairCost = ['Excellent', 'Good'].includes(newCondition);
                                                            setNewItem(prev => ({
                                                                ...prev,
                                                                condition: newCondition,
                                                                repairCost: resetRepairCost ? 0 : prev.repairCost,
                                                                requiresRepair: resetRepairCost ? false : prev.requiresRepair
                                                            }));
                                                        }}
                                                    >
                                                        <option value="Excellent">Excellent</option>
                                                        <option value="Good">Good</option>
                                                        <option value="Fair">Fair</option>
                                                        <option value="Poor">Poor</option>
                                                        <option value="Damaged">Damaged</option>
                                                        <option value="Non-functional">Non-functional</option>
                                                    </select>
                                                    <small className="text-muted">Current condition of the item during move-out</small>
                                                </div>

                                                <div className="mb-3">
                                                    <label className="form-label">Repair/Replacement Cost</label>
                                                    <div className="input-group">
                                                        {newItem.currencyId && (
                                                            <span className="input-group-text">
                                                                {currencies.find(curr => curr._id === newItem.currencyId)?.currencyShortCode || '$'}
                                                            </span>
                                                        )}
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            className="form-control"
                                                            value={newItem.repairCost || 0}
                                                            onChange={(e) => {
                                                                const cost = parseFloat(e.target.value) || 0;
                                                                setNewItem(prev => ({
                                                                    ...prev,
                                                                    repairCost: cost,
                                                                    requiresRepair: cost > 0
                                                                }));
                                                            }}
                                                            placeholder="0.00"
                                                            disabled={['Excellent', 'Good'].includes(newItem.condition)}
                                                        />
                                                    </div>
                                                    <small className="text-muted">
                                                        {['Excellent', 'Good'].includes(newItem.condition)
                                                            ? 'No repair cost for items in excellent/good condition'
                                                            : 'Enter cost to repair or replace this item. This will be added to security deposit deductions.'}
                                                    </small>
                                                </div>

                                                {newItem.repairCost > 0 && (
                                                    <div className="mb-3">
                                                        <label className="form-label">Repair Description</label>
                                                        <textarea
                                                            className="form-control"
                                                            value={newItem.repairDescription || ''}
                                                            onChange={(e) => setNewItem(prev => ({ ...prev, repairDescription: e.target.value }))}
                                                            placeholder="Describe the repairs needed, damage found, or replacement required..."
                                                            rows="3"
                                                        ></textarea>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        <div className="mb-3">
                                            <label className="form-label">Default Condition <span className="text-danger">*</span></label>
                                            <p className="small text-muted mb-2">Select which condition should be the default</p>

                                            <div className="d-flex flex-wrap gap-3 mt-3">
                                                {defaultConditions.map((condition, index) => (
                                                    <div key={index} className="form-check me-3">
                                                        <input
                                                            type="radio"
                                                            className="form-check-input"
                                                            name="defaultCondition"
                                                            id={`default-${index}`}
                                                            checked={newItem.defaultCondition === condition}
                                                            onChange={() => setAsDefault(condition)}
                                                        />
                                                        <label className="form-check-label" htmlFor={`default-${index}`}>
                                                            {condition}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label">Item Images</label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                multiple
                                                accept="image/*"
                                                onChange={(e) => {
                                                    const files = Array.from(e.target.files);
                                                    const imageData = files.map(file => ({
                                                        file: file,
                                                        preview: URL.createObjectURL(file),
                                                        name: file.name,
                                                        size: file.size
                                                    }));
                                                    setNewItem(prev => ({
                                                        ...prev,
                                                        images: [...(prev.images || []), ...imageData]
                                                    }));
                                                }}
                                            />
                                            {newItem.images && newItem.images.length > 0 && (
                                                <div className="mt-2">
                                                    <small className="text-muted">{newItem.images.length} image(s) selected</small>
                                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                                        {newItem.images.map((imageData, index) => {
                                                            // For new uploads, use preview URL; for existing, prepend backend URL
                                                            const imageUrl = imageData.preview ||
                                                                (typeof imageData === 'string' ? `${backend_url}${imageData}` : imageData);

                                                            return (
                                                                <div key={index} className="position-relative">
                                                                    <img
                                                                        src={imageUrl}
                                                                        alt={`Preview ${index + 1}`}
                                                                        style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                                                        className="rounded border"
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-danger position-absolute top-0 end-0"
                                                                        style={{ transform: 'translate(50%, -50%)', padding: '2px 6px' }}
                                                                        onClick={() => {
                                                                            const newImages = [...newItem.images];
                                                                            newImages.splice(index, 1);
                                                                            setNewItem(prev => ({ ...prev, images: newImages }));
                                                                        }}
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="form-check mb-3">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={newItem.isRequired}
                                                onChange={(e) =>
                                                    setNewItem({ ...newItem, isRequired: e.target.checked })
                                                }
                                                id="isRequired"
                                            />
                                            <label className="form-check-label" htmlFor="isRequired">
                                                Required Item
                                            </label>
                                        </div>
                                    </form>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setModalOpen(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={saveInspectionItem}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                Saving...
                                            </>
                                        ) : (
                                            isEditing ? "Update Item" : "Save Item"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Modal Backdrop */}
                    <div className="modal-backdrop fade show"></div>
                </>
            )}
        </div>
    );
};

export default HandoverInspectionTab;